import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Loader2, CheckCircle2, ClipboardCheck, ClipboardList, PlusCircle, Star,
  Users, Flame, Calendar, FileText, Sparkles, ListChecks,
} from "lucide-react";
import MobileShell from "../components/MobileShell";
import ComplaintsWidget from "../components/ComplaintsWidget";
import {
  getTodayAttendance, getTodayProgress, listTests,
  listMyAttendance, listMyProgressHistory, getMyTeachingDay,
  currentTeacher,
  type AttendanceRow, type PeriodLogRow, type TestRow, type DaySlot,
} from "../services/teacherApi";
import { istDayKey, istTodayKey, formatLocalDate } from "../utils/dates";

// Landing dashboard for the mobile teacher portal — the screen a teacher sees
// the moment they unlock the app each morning:
//
//   1. Today's snapshot — check-in time, today's logged period, pending + live
//                         counts. The "what does today look like?" row.
//   2. Today's classes  — the day's teaching, straight from the routine, with
//                         what's been logged and what hasn't.
//   3. Quick actions    — buttons fanning out to the heaviest-used pages.
//   4. Complaints       — the open-complaints widget.
//   5. Weekly activity  — last-7-day rollup of attendance + progress logs.
//
// (Badges / Curriculum progress / Quick stats panels were removed pre-launch —
// they rendered placeholder/stub data with no backend aggregation behind them.
// Test library snapshot and Pending work were removed later: the test surfaces
// live in the Library tab, and the dashboard is about the teaching day.)

interface DashboardData {
  attendance: { check_in_at: string | null; check_out_at: string | null };
  todayProgress: PeriodLogRow[];
  tests: TestRow[];
  weekAttendance: AttendanceRow[];
  weekProgressCount: number;
  weekTestsCreated: number;
  streakDays: number;
  // Today's teaching, from the routine (+ any cover duty). `dayConfigured`
  // false means this teacher has no routine yet, which reads very differently
  // from "no classes today" and is shown as such.
  daySlots: DaySlot[];
  dayConfigured: boolean;
}

export default function DashboardPage() {
  const teacher = currentTeacher();
  const [data, setData] = useState<DashboardData | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    // One Promise.all so the dashboard hydrates in a single network round-trip
    // instead of waterfalling each panel's fetch. Any single endpoint failing
    // surfaces the error banner; the rest still render with their own empty
    // states from `null`.
    (async () => {
      try {
        const [att, prog, tests, monthAtt, monthProg, day] = await Promise.all([
          getTodayAttendance().catch(() => null),
          getTodayProgress().catch(() => ({ rows: [], work_date: "" })),
          listTests().catch(() => [] as TestRow[]),
          listMyAttendance().catch(() => ({ rows: [], month: "" })),
          listMyProgressHistory().catch(() => ({ rows: [], month: "" })),
          getMyTeachingDay().catch(() => ({ slots: [] as DaySlot[], configured: false, date: "" })),
        ]);

        const today = istTodayKey();
        const weekAtt = filterLast7Days(monthAtt.rows, today);
        const weekProgRows = filterLast7Days(monthProg.rows, today, (r) => r.work_date);
        const weekTests = tests.filter(t => {
          if (!t.date || !t.mine) return false;
          const k = istDayKey(t.date);
          return !!k && isWithinLast7Days(k, today);
        });

        setData({
          attendance: {
            check_in_at: att?.today?.check_in_at ?? null,
            check_out_at: att?.today?.check_out_at ?? null,
          },
          todayProgress: prog.rows,
          tests,
          weekAttendance: weekAtt,
          weekProgressCount: weekProgRows.length,
          weekTestsCreated: weekTests.length,
          streakDays: computeAttendanceStreak(monthAtt.rows, today),
          daySlots: day.slots,
          dayConfigured: day.configured,
        });
      } catch (e: any) {
        setErr(e?.message || "Failed to load dashboard");
      }
    })();
  }, []);

  return (
    <MobileShell title={teacher ? `Hi, ${teacher.name.split(" ")[0]}` : "Dashboard"}>
      {!data && !err && (
        <div className="flex items-center gap-2 text-slate-500 text-sm py-8 justify-center">
          <Loader2 size={16} className="animate-spin"/> Loading…
        </div>
      )}
      {err && (
        <div className="text-sm text-madder-400 bg-madder-500/10 border border-madder-500/30 rounded-lg px-4 py-3 mb-4">
          {err}
        </div>
      )}

      {data && (
        <div className="grid gap-4 pb-4">
          <TodaySnapshot data={data}/>
          {/* The day's teaching, straight from the routine — what the teacher
              opens the app to find out. Sits directly under the snapshot
              because it's the answer to "what am I doing today?". */}
          <TodayClasses slots={data.daySlots} configured={data.dayConfigured}/>
          <QuickActions/>
          <ComplaintsWidget/>
          <WeeklyActivity data={data}/>
        </div>
      )}
    </MobileShell>
  );
}

// ── Today's snapshot ───────────────────────────────────────────────────────
function TodaySnapshot({ data }: { data: DashboardData }) {
  const checkedIn = !!data.attendance.check_in_at;
  const liveCount   = data.tests.filter(t => classifyTab(t) === "live").length;
  // Pending = "submitted but not yet reviewed" across visible tests. The list
  // endpoint doesn't break out submitted-vs-reviewed, so we approximate by
  // `students - reviewed` on non-closed tests. Overestimates slightly (counts
  // students who haven't started yet), but it's the right order of magnitude
  // for a glance-able pill — and a click takes the teacher to the per-test
  // breakdown where the truth lives.
  const pending = data.tests
    .filter(t => t.status !== "closed" && t.mine)
    .reduce((n, t) => n + Math.max(0, (t.students || 0) - (t.reviewed || 0)), 0);
  // First period logged today, if any — gives the dashboard a "you taught Ch-3
  // first thing" line without making the teacher open Progress.
  const firstPeriod = data.todayProgress[0];

  return (
    <Section title="Today's snapshot">
      <div className="space-y-2.5 text-sm">
        <Line>
          <CheckCircle2 size={14} className={checkedIn ? "text-emerald-400" : "text-slate-600"}/>
          <span className="text-slate-300">Checked in</span>
          <span className="ml-auto font-mono text-slate-200">
            {checkedIn ? formatHM(data.attendance.check_in_at!) : "Not yet"}
          </span>
        </Line>
        <Line>
          <FileText size={14} className="text-cobalt-400"/>
          <span className="text-slate-300">Progress logged</span>
          <span className="ml-auto text-slate-200 truncate text-right">
            {firstPeriod
              ? `${firstPeriod.subject_name} · Ch-${firstPeriod.chapter_number}`
              : "Nothing yet"}
          </span>
        </Line>
        <Line>
          <ListChecks size={14} className="text-violet-300"/>
          <span className="text-slate-300">To review</span>
          <span className="ml-auto font-mono text-white">{pending}</span>
        </Line>
        <Line>
          <Sparkles size={14} className="text-emerald-400"/>
          <span className="text-slate-300">Live tests</span>
          <span className="ml-auto font-mono text-white">{liveCount}</span>
        </Line>
      </div>
    </Section>
  );
}

// ── Today's classes ───────────────────────────────────────────────────────
// Straight from the routine: every period this teacher is scheduled for today,
// including any they've picked up covering for an absent colleague. Each row
// shows whether it's been logged yet, and tapping through goes to Progress
// where it can be.
//
// A few minutes after each period ends, services/progress_reminders.ts pushes
// "Period 3 — what did you teach?" for anything still unlogged, so this panel
// and that notification are two views of the same list.
function TodayClasses({ slots, configured }: { slots: DaySlot[]; configured: boolean }) {
  const done = slots.filter(s => s.logged).length;

  return (
    <Section
      title="Today's classes"
      action={
        <Link to="/progress" className="text-[11px] font-mono text-cobalt-400 hover:text-cobalt-300 uppercase tracking-widest">
          Log →
        </Link>
      }>
      {!configured ? (
        <p className="text-xs text-slate-400">
          No routine set for you yet. Once an admin builds it, your day shows up here.
        </p>
      ) : slots.length === 0 ? (
        <p className="text-xs text-slate-400">No classes scheduled for you today.</p>
      ) : (
        <>
          <div className="text-[11px] font-mono text-slate-500 mb-2">
            {done}/{slots.length} logged
          </div>
          <ul className="space-y-1.5">
            {slots.map(s => (
              <li key={`${s.source}-${s.period_no}`}>
                <Link to="/progress"
                  className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg border border-white/10 hover:bg-white/[0.04] transition-colors">
                  <span className="shrink-0 w-8 h-8 rounded-md bg-white/[0.03] border border-white/10 flex flex-col items-center justify-center font-mono leading-none">
                    <span className="text-[11px] text-cobalt-300">{s.period_no}</span>
                    {s.starts_at && <span className="text-[7px] text-slate-500 mt-0.5">{s.starts_at}</span>}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm text-white truncate">
                      {s.class_label}{s.subject_name ? ` · ${s.subject_name}` : ""}
                    </span>
                    {s.source === "cover" && (
                      <span className="block text-[10px] text-ochre-400 truncate">
                        Covering for {s.absent_teacher_name}
                      </span>
                    )}
                  </span>
                  {s.logged
                    ? <CheckCircle2 size={14} className="shrink-0 text-emerald-400"/>
                    : <PlusCircle size={14} className="shrink-0 text-slate-600"/>}
                </Link>
              </li>
            ))}
          </ul>
        </>
      )}
    </Section>
  );
}

// ── Quick actions ─────────────────────────────────────────────────────────
function QuickActions() {
  // Six buttons filling a 3-col grid exactly — the heaviest-used surfaces.
  // Order is by "I just unlocked the app, what's most likely next?": punch in,
  // take the roll, log the class I just taught, prep / create the next test,
  // then students + doubts. Library lives in the bottom tab bar, so it's
  // intentionally not repeated here.
  const items: Array<{ to: string; label: string; icon: React.ReactNode; tint: string }> = [
    { to: "/attendance", label: "Check in",    icon: <CheckCircle2 size={18}/>, tint: "text-emerald-400" },
    { to: "/student-attendance", label: "Take roll", icon: <ClipboardCheck size={18}/>, tint: "text-emerald-400" },
    { to: "/progress",   label: "Log progress",icon: <FileText size={18}/>,     tint: "text-cobalt-400" },
    { to: "/create",     label: "Create test", icon: <PlusCircle size={18}/>,   tint: "text-cobalt-400" },
    { to: "/students",   label: "Students",    icon: <Users size={18}/>,        tint: "text-violet-300" },
    { to: "/doubts",     label: "Doubts",      icon: <Star size={18}/>,         tint: "text-ochre-400" },
  ];
  return (
    <Section title="Quick actions">
      <div className="grid grid-cols-3 gap-2">
        {items.map(it => (
          <Link key={it.to + it.label} to={it.to}
            className="flex flex-col items-center gap-1.5 py-3 px-2 rounded-lg border border-white/10 hover:bg-white/[0.04] transition-colors">
            <span className={it.tint}>{it.icon}</span>
            <span className="text-[10px] font-mono uppercase tracking-widest text-slate-300 text-center leading-tight">
              {it.label}
            </span>
          </Link>
        ))}
      </div>
    </Section>
  );
}

// ── Weekly activity ───────────────────────────────────────────────────────
function WeeklyActivity({ data }: { data: DashboardData }) {
  // Render a "✓ ✓ ✓ ✗ ✓ ✓ ✓" strip for the last 7 calendar days, with today
  // on the right. Each cell is either "present" (any attendance row that
  // day), or "absent". Sunday-only schools will look weird; that's fine for v1.
  const presentDays = new Set(data.weekAttendance.map(r => r.work_date));
  const today = istTodayKey();
  const days = lastNDays(7, today);

  return (
    <Section title="This week">
      <div className="space-y-2.5 text-sm">
        <Line>
          <Calendar size={14} className="text-emerald-400"/>
          <span className="text-slate-300">Attendance</span>
          <span className="ml-auto flex items-center gap-0.5">
            {days.map(d => (
              <span key={d} title={d}
                className={`w-3.5 h-3.5 grid place-items-center text-[8px] font-mono rounded-sm ${
                  presentDays.has(d)
                    ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40"
                    : "bg-white/[0.03] text-slate-600 border border-white/10"
                }`}>
                {presentDays.has(d) ? "✓" : "✗"}
              </span>
            ))}
            <span className="ml-2 font-mono text-[10px] text-slate-400">{presentDays.size}/7</span>
          </span>
        </Line>
        <Line>
          <FileText size={14} className="text-cobalt-400"/>
          <span className="text-slate-300">Progress entries</span>
          <span className="ml-auto font-mono text-white">{data.weekProgressCount}</span>
        </Line>
        <Line>
          <PlusCircle size={14} className="text-cobalt-400"/>
          <span className="text-slate-300">Tests created</span>
          <span className="ml-auto font-mono text-white">{data.weekTestsCreated}</span>
        </Line>
        <Line>
          <Flame size={14} className="text-ochre-400"/>
          <span className="text-slate-300">Attendance streak</span>
          <span className="ml-auto font-mono text-white">{data.streakDays} day{data.streakDays === 1 ? "" : "s"}</span>
        </Line>
      </div>
    </Section>
  );
}

// ── Primitives ────────────────────────────────────────────────────────────

function Section({ title, action, children }: {
  title: string; action?: React.ReactNode; children: React.ReactNode;
}) {
  return (
    <section className="glass-panel rounded-xl px-4 py-3.5">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-mono text-[10px] text-cobalt-400 uppercase tracking-widest">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

function Line({ children }: { children: React.ReactNode }) {
  return <div className="flex items-center gap-2">{children}</div>;
}

function CountChip({ label, value, color }: { label: string; value: number; color: "emerald" | "cobalt" | "slate" }) {
  const cls =
    color === "emerald" ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-300" :
    color === "cobalt"  ? "bg-cobalt-500/10 border-cobalt-500/40 text-cobalt-300" :
                          "bg-white/[0.03] border-white/10 text-slate-300";
  return (
    <div className={`flex flex-col items-center justify-center py-2.5 rounded-lg border ${cls}`}>
      <div className="font-semibold text-lg leading-none">{value}</div>
      <div className="text-[10px] font-mono uppercase tracking-widest mt-1 opacity-80">{label}</div>
    </div>
  );
}

// ── helpers ───────────────────────────────────────────────────────────────

function classifyTab(t: TestRow): "live" | "upcoming" | "past" | null {
  // Mirror LibraryPage's classification so dashboard counts match what the
  // teacher sees on the next screen they open.
  if (t.is_expired) return "past";
  if (t.status === "closed" || (t.reviewed > 0 && t.reviewed >= t.students)) return "past";
  if (t.date) {
    const scheduled = istDayKey(t.date);
    if (scheduled && scheduled > istTodayKey()) return "upcoming";
  }
  if (t.status === "draft") return null;
  return "live";
}

function formatHM(iso: string): string {
  try {
    return new Intl.DateTimeFormat("en-IN", {
      hour: "numeric", minute: "2-digit", hour12: false,
      timeZone: "Asia/Kolkata",
    }).format(new Date(iso));
  } catch {
    return iso.slice(11, 16);
  }
}

function filterLast7Days<T>(rows: T[], today: string, keyFn: (r: T) => string = (r: any) => r.work_date): T[] {
  return rows.filter(r => isWithinLast7Days(keyFn(r), today));
}

function isWithinLast7Days(dayKey: string, today: string): boolean {
  // String-lex date math is safe because both are "YYYY-MM-DD". 7-day window
  // ending TODAY inclusive — so 6 days ago through today.
  if (!dayKey) return false;
  const oldest = addDays(today, -6);
  return dayKey >= oldest && dayKey <= today;
}

function lastNDays(n: number, today: string): string[] {
  return Array.from({ length: n }, (_, i) => addDays(today, -(n - 1 - i)));
}

function addDays(dayKey: string, delta: number): string {
  const [y, m, d] = dayKey.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + delta);
  return dt.toISOString().slice(0, 10);
}

// Consecutive days of attendance ending on TODAY. If today has no row, we drop
// to yesterday — many teachers haven't checked in yet when they open the
// dashboard, and resetting the streak to 0 every morning would be cruel.
function computeAttendanceStreak(rows: AttendanceRow[], today: string): number {
  const present = new Set(rows.map(r => r.work_date));
  let cursor = today;
  if (!present.has(cursor)) cursor = addDays(cursor, -1);
  let n = 0;
  while (present.has(cursor)) {
    n++;
    cursor = addDays(cursor, -1);
  }
  return n;
}
